import { getBaseApi } from "../BaseApi";

export interface StockSummaryResponse {
  productCatalogByType: {
    expedition: number;
    affiliate: number;
    request: number;
    other: number;
    total: number;
  };
  products: {
    count: number;
    totalQuantity: number;
    totalRest: number;
    totalProvided: number;
    totalMissing: number;
    lowStockCount: number;
  };
  affiliates: {
    count: number;
    totalQuantity: number;
    totalRest: number;
    totalProvided: number;
    lowStockCount: number;
  };
  expeditions: {
    count: number;
    totalQuantity: number;
    totalDamaged: number;
  };
  requests: {
    count: number;
    pendingCount: number;
    confirmedCount: number;
  };
  global: {
    totalRestProducts: number;
    totalRestAffiliates: number;
  };
  topConfirmedProduct?: {
    _id: string;
    name: string;
    count: number;
  } | null;
  topDeliveredProduct?: {
    _id: string;
    name: string;
    count: number;
  } | null;
  shippingBreakdown?: Record<string, number>;
}

/** Valeurs par défaut si l’API est partielle ou ancienne (évite écran blanc / crash). */
export const EMPTY_STOCK_SUMMARY: StockSummaryResponse = {
  productCatalogByType: {
    expedition: 0,
    affiliate: 0,
    request: 0,
    other: 0,
    total: 0,
  },
  products: {
    count: 0,
    totalQuantity: 0,
    totalRest: 0,
    totalProvided: 0,
    totalMissing: 0,
    lowStockCount: 0,
  },
  affiliates: {
    count: 0,
    totalQuantity: 0,
    totalRest: 0,
    totalProvided: 0,
    lowStockCount: 0,
  },
  expeditions: {
    count: 0,
    totalQuantity: 0,
    totalDamaged: 0,
  },
  requests: {
    count: 0,
    pendingCount: 0,
    confirmedCount: 0,
  },
  global: {
    totalRestProducts: 0,
    totalRestAffiliates: 0,
  },
  topConfirmedProduct: null,
  topDeliveredProduct: null,
};

export function normalizeStockSummary(
  raw: Partial<StockSummaryResponse> | null | undefined
): StockSummaryResponse {
  const e = EMPTY_STOCK_SUMMARY;
  if (!raw || typeof raw !== "object") {
    return { ...e };
  }
  const pctIn: Partial<StockSummaryResponse["productCatalogByType"]> =
    raw.productCatalogByType ?? {};
  const expedition = Number(pctIn.expedition) || 0;
  const affiliate = Number(pctIn.affiliate) || 0;
  const request = Number(pctIn.request) || 0;
  const other = Number(pctIn.other) || 0;
  let total = Number(pctIn.total);
  if (!Number.isFinite(total) || total < 0) {
    total = expedition + affiliate + request + other;
  }
  const num = (v: unknown, d: number) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : d;
  };
  const rp = { ...e.products, ...(raw.products || {}) };
  const ra = { ...e.affiliates, ...(raw.affiliates || {}) };
  const rx = { ...e.expeditions, ...(raw.expeditions || {}) };
  const rq = { ...e.requests, ...(raw.requests || {}) };
  const rg = { ...e.global, ...(raw.global || {}) };

  return {
    productCatalogByType: {
      expedition,
      affiliate,
      request,
      other,
      total,
    },
    products: {
      count: num(rp.count, 0),
      totalQuantity: num(rp.totalQuantity, 0),
      totalRest: num(rp.totalRest, 0),
      totalProvided: num(rp.totalProvided, 0),
      totalMissing: num(rp.totalMissing, 0),
      lowStockCount: num(rp.lowStockCount, 0),
    },
    affiliates: {
      count: num(ra.count, 0),
      totalQuantity: num(ra.totalQuantity, 0),
      totalRest: num(ra.totalRest, 0),
      totalProvided: num(ra.totalProvided, 0),
      lowStockCount: num(ra.lowStockCount, 0),
    },
    expeditions: {
      count: num(rx.count, 0),
      totalQuantity: num(rx.totalQuantity, 0),
      totalDamaged: num(rx.totalDamaged, 0),
    },
    requests: {
      count: num(rq.count, 0),
      pendingCount: num(rq.pendingCount, 0),
      confirmedCount: num(rq.confirmedCount, 0),
    },
    global: {
      totalRestProducts: num(rg.totalRestProducts, 0),
      totalRestAffiliates: num(rg.totalRestAffiliates, 0),
    },
    topConfirmedProduct: raw.topConfirmedProduct || null,
    topDeliveredProduct: raw.topDeliveredProduct || null,
  };
}

/** Agrégations lourdes : délai plus long que le défaut Axios (30s). */
const STOCK_SUMMARY_REQUEST_MS = 120_000;

function getStockSummary(country?: string) {
  const params = country ? { country } : undefined;
  return getBaseApi().axiosInstance.get("stock/summary", {
    params,
    timeout: STOCK_SUMMARY_REQUEST_MS,
  });
}

export { getStockSummary };
